// Big maps (issue 1172, BOARD-010): a map wider or taller than `PICTURE_LIMITS.maxPx` is never drawn whole. At upload the
// server builds its PYRAMID once — level 0 the picture at full size, each level above it half the one below (rounded
// down, as libvips halves), until a level fits one tile — and the board draws only the tiles in view, from the level
// that matches the zoom. The geometry lives here so the server that cuts and the board that places cannot disagree.
//
// A level-k tile (x, y) is the rectangle [x·T, (x+1)·T) × [y·T, (y+1)·T) of that level's pixels, clipped to the level;
// on the board it covers those pixels scaled by the map's size over the level's (`scaleX`, `scaleY`), so every level
// spans the whole map exactly and tiles of one level meet edge to edge, with no gap and no overlap.
import { PICTURE_LIMITS } from './pictures.js';
/** A pyramid tile's side in its level's pixels. */
export const PYRAMID_TILE = 512;
/** A map drawn from its pyramid: a side over the old whole-picture limit. Smaller maps stay one picture, as before. */
export const isTiledMap = (m) => !!m && Math.max(m.width, m.height) > PICTURE_LIMITS.maxPx;
/** Every level of a picture's pyramid, full size first, the last the first to fit one tile. */
export function pyramidLevels(width, height, tile = PYRAMID_TILE) {
    const out = [];
    let w = width;
    let h = height;
    for (let k = 0;; k += 1) {
        out.push({ k, width: w, height: h, cols: Math.ceil(w / tile), rows: Math.ceil(h / tile), scaleX: width / w, scaleY: height / h });
        if ((w <= tile && h <= tile) || w < 2 || h < 2)
            break;
        w = Math.floor(w / 2);
        h = Math.floor(h / 2);
    }
    return out;
}
/** A tile's key on the wire and in the board's slots: "k/x,y". */
export const pyramidKey = (k, x, y) => `${k}/${x},${y}`;
export function parsePyramidKey(key) {
    const m = /^(\d{1,2})\/(\d{1,4}),(\d{1,4})$/.exec(key);
    return m ? { k: Number(m[1]), x: Number(m[2]), y: Number(m[3]) } : null;
}
/** The tile's rectangle in its level's pixels, or null for a tile beyond the level. */
export function pyramidTileBox(level, x, y, tile = PYRAMID_TILE) {
    if (x < 0 || y < 0 || x >= level.cols || y >= level.rows)
        return null;
    const left = x * tile;
    const top = y * tile;
    return { left, top, width: Math.min(tile, level.width - left), height: Math.min(tile, level.height - top) };
}
/** The tile's rectangle on the board (board pixels): where its sprite is placed and how big. */
export function pyramidBoardRect(level, x, y, tile = PYRAMID_TILE) {
    const b = pyramidTileBox(level, x, y, tile);
    if (!b)
        return null;
    const x0 = b.left * level.scaleX;
    const y0 = b.top * level.scaleY;
    return { x: x0, y: y0, width: (b.left + b.width) * level.scaleX - x0, height: (b.top + b.height) * level.scaleY - y0 };
}
/**
 * The level the board draws at this zoom (screen pixels per board pixel, times the device's pixel ratio): the coarsest
 * whose pixels are still no bigger than a screen pixel, so nothing is drawn blurred and nothing more is fetched than
 * the screen can show.
 */
export function pyramidLevelFor(levels, zoom) {
    let pick = levels[0];
    for (const l of levels)
        if (Math.max(l.scaleX, l.scaleY) * zoom <= 1.0001)
            pick = l;
    return pick;
}
/** The tiles of one level that touch a rectangle of the board. */
export function pyramidTilesIn(level, rect, tile = PYRAMID_TILE) {
    const x0 = Math.max(0, Math.floor(rect.x / level.scaleX / tile));
    const y0 = Math.max(0, Math.floor(rect.y / level.scaleY / tile));
    const x1 = Math.min(level.cols - 1, Math.floor((rect.x + rect.width) / level.scaleX / tile));
    const y1 = Math.min(level.rows - 1, Math.floor((rect.y + rect.height) / level.scaleY / tile));
    const out = [];
    for (let y = y0; y <= y1; y += 1)
        for (let x = x0; x <= x1; x += 1)
            out.push({ x, y });
    return out;
}
//# sourceMappingURL=pyramid.js.map